import React, { useContext, useEffect } from 'react';
import {
  Avatar,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { Check, MemoryOutlined, RouterOutlined, SensorsOutlined } from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import zod from 'zod';
import TextFieldComponent from '../../../../components/TextFieldComponent';
import ButtonComponent from '../../../../components/ButtonComponent';
import { AppContext } from '../../../../context/AppContextProvider';
import { ISportsDeviceFormData } from '../types';

const deviceSchema = zod.object({
  externalControlId: zod.string().min(1, 'ID do módulo é obrigatório'),
  moduleIp: zod.string().optional(),
});

type DeviceFormInput = zod.infer<typeof deviceSchema>;

interface DeviceFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
  initialData?: ISportsDeviceFormData | null;
  isLoading: boolean;
}

const DeviceFormModal: React.FC<DeviceFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  isLoading,
}) => {
  const appContext = useContext(AppContext);
  const showSnackbar = appContext?.showSnackbar || (() => {});
  const setIsShowLoadingComponent = appContext?.setIsShowLoadingComponent || (() => {});

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<DeviceFormInput>({
    resolver: zodResolver(deviceSchema),
    defaultValues: {
      externalControlId: '',
    },
  });

  useEffect(() => {
    reset({
      externalControlId: initialData?.externalControlId || '',
      moduleIp: initialData?.moduleIp || '',
    });
  }, [initialData, reset]);

  const onSubmit = async (data: DeviceFormInput) => {
    try {
      setIsShowLoadingComponent(true);
      await onSave(data);
      reset();
      onClose();
      showSnackbar('Dispositivo atualizado com sucesso!', 'success');
    } catch (error: any) {
      showSnackbar(error?.message || 'Erro ao salvar dispositivo.', 'error');
    } finally {
      setIsShowLoadingComponent(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
    >
      <DialogTitle sx={{ pb: 1.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <MemoryOutlined />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="bold" lineHeight={1.2}>
              {initialData?.name || 'Dispositivo'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Vincular ao módulo físico (botão RF)
            </Typography>
          </Box>
        </Stack>
        <Divider sx={{ mt: 2 }} />
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Informe o ID do módulo cadastrado no controlador físico para vincular
          este dispositivo a ele. Nenhuma outra informação do dispositivo será alterada.
        </Typography>

        <Controller
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextFieldComponent
              label="ID do Módulo (botão RF)"
              value={value || ''}
              onChangeText={onChange}
              errorMessage={error?.message}
              placeholder="Ex: 97015-530"
              iconStart={<SensorsOutlined fontSize="small" />}
              required
              autoFocus
              fullWidth
              mb={2}
            />
          )}
          name="externalControlId"
          control={control}
        />

        <Controller
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextFieldComponent
              label="IP do Módulo"
              value={value || ''}
              onChangeText={onChange}
              errorMessage={error?.message}
              placeholder="Ex: 191.252.156.175"
              iconStart={<RouterOutlined fontSize="small" />}
              isOptional
              fullWidth
            />
          )}
          name="moduleIp"
          control={control}
        />
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <ButtonComponent color="error" onClick={onClose}>
          Cancelar
        </ButtonComponent>
        <ButtonComponent
          onClick={handleSubmit(onSubmit)}
          loading={isLoading}
          disabled={!isDirty}
          startIcon={<Check />}
        >
          Atualizar
        </ButtonComponent>
      </DialogActions>
    </Dialog>
  );
};

export default DeviceFormModal;
